/**
 * MFA Verification Component
 * Handles TOTP code verification during login
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  Shield,
  AlertCircle,
  ArrowLeft,
  Key,
  Smartphone
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MFAVerificationProps {
  factorId: string;
  onSuccess: () => void;
  onCancel?: () => void;
  onUseRecoveryCode?: () => void;
}

type VerificationMode = 'totp' | 'recovery';

export function MFAVerification({
  factorId,
  onSuccess,
  onCancel,
  onUseRecoveryCode
}: MFAVerificationProps) {
  const [mode, setMode] = useState<VerificationMode>('totp');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (mode === 'totp' && code.length === 6) {
      verifyTOTP();
    }
  }, [code]);

  // Verify TOTP code
  const verifyTOTP = async () => {
    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });

      if (challengeError) {
        throw challengeError;
      }

      // Then verify with the code
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Verification Successful',
        description: 'Welcome back!',
      });

      onSuccess();
    } catch (err: any) {
      console.error('MFA verification error:', err);
      setError(err.message || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify recovery code
  const verifyRecoveryCode = async () => {
    if (!recoveryCode.trim()) {
      setError('Please enter a recovery code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-recovery-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ recoveryCode: recoveryCode.trim() }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Invalid recovery code');
      }

      // Show warning if running low on codes
      if (result.warning) {
        toast({
          title: 'Recovery Code Verified',
          description: `${result.warning}. ${result.remainingCodes} codes remaining.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Recovery Code Verified',
          description: `Access granted. ${result.remainingCodes} codes remaining.`,
        });
      }

      onSuccess();

    } catch (err: any) {
      console.error('Recovery code error:', err);
      setError(err.message || 'Invalid recovery code. Please try again.');
      setRecoveryCode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto shadow-xl">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            {mode === 'totp' ? (
              <Smartphone className="w-8 h-8 text-primary" />
            ) : (
              <Key className="w-8 h-8 text-primary" />
            )}
          </div>
        </div>
        <CardTitle>
          {mode === 'totp' ? 'Two-Factor Authentication' : 'Recovery Code'}
        </CardTitle>
        <CardDescription>
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app'
            : 'Enter one of your recovery codes'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {mode === 'totp' ? (
          <>
            {/* TOTP Code Input */}
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={setCode}
                disabled={isLoading}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {/* Error Message */}
            {error && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {/* Verify Button */}
            <Button
              className="w-full"
              onClick={verifyTOTP}
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            {/* Switch to Recovery Code */}
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                onClick={() => {
                  setMode('recovery');
                  setError(null);
                  setCode('');
                }}
              >
                Lost access to your authenticator?{' '}
                <span className="text-primary font-medium">Use a recovery code</span>
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Recovery Code Input */}
            <div className="space-y-2">
              <Label htmlFor="recovery-code">Recovery Code</Label>
              <Input
                id="recovery-code"
                type="text"
                placeholder="XXXXX-XXXXX"
                value={recoveryCode}
                onChange={(e) => {
                  setRecoveryCode(e.target.value.toUpperCase());
                  setError(null);
                }}
                disabled={isLoading}
                className="font-mono text-center text-lg tracking-wider"
                autoFocus
              />
            </div>

            {/* Error Message */}
            {error && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {/* Verify Button */}
            <Button
              className="w-full"
              onClick={verifyRecoveryCode}
              disabled={isLoading || !recoveryCode.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Recovery Code'
              )}
            </Button>

            {/* Switch back to TOTP */}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setMode('totp');
                setError(null);
                setRecoveryCode('');
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to authenticator
            </Button>
          </>
        )}

        {/* Cancel Button */}
        {onCancel && (
          <Button
            variant="outline"
            className="w-full"
            onClick={onCancel}
          >
            Cancel Sign In
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Standalone MFA Verification Page Component
 * For use in routing
 */
export function MFAVerificationPage() {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkMFAFactors();
  }, []);

  const checkMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        throw error;
      }

      // Find verified TOTP factor
      const totpFactor = data.totp.find(f => f.status === 'verified');

      if (totpFactor) {
        setFactorId(totpFactor.id);
      } else {
        setError('No MFA factor found. Please set up MFA first.');
      }
    } catch (err: any) {
      console.error('Error checking MFA factors:', err);
      setError(err.message || 'Failed to load MFA information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = () => {
    // Redirect to dashboard after successful verification
    window.location.href = '/dashboard';
  };

  const handleCancel = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !factorId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error || 'MFA not configured'}</AlertDescription>
            </Alert>
            <Button className="w-full mt-4" onClick={handleCancel}>
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/logo-square.svg"
              alt="Prozpector"
              className="w-16 h-16 rounded-xl shadow-lg"
            />
          </div>
          <img
            src="/logo-wordmark.svg"
            alt="Prozpector"
            className="h-10 mx-auto"
          />
        </div>

        <MFAVerification
          factorId={factorId}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Secure authentication powered by Supabase</p>
        </div>
      </div>
    </div>
  );
}

export default MFAVerification;
